import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { FormField } from "@/components/ui/FormField";

export const roleSchema = z.object({
  Name: z.string().min(2, "Role name must be at least 2 characters"),
});

export type RoleFormData = z.infer<typeof roleSchema>;

export interface AddRoleFormHandle {
  submit: () => void;
  setFieldError: (field: string, message: string) => void;
  clearFieldErrors: () => void;
}

interface AddRoleFormProps {
  onSubmit: (data: RoleFormData) => void;
  defaultValues?: RoleFormData;
  isLoading?: boolean;
}

const AddRoleForm = forwardRef<AddRoleFormHandle, AddRoleFormProps>(
  ({ onSubmit, defaultValues, isLoading }, ref) => {
    const {
      control,
      handleSubmit,
      formState: { errors },
      clearErrors,
      setError,
      reset,
    } = useForm<RoleFormData>({
      resolver: zodResolver(roleSchema),
      defaultValues: defaultValues || {
        Name: "",
      },
    });

    // Reset form when defaultValues change (for edit mode)
    useEffect(() => {
      if (defaultValues) {
        reset(defaultValues);
      }
    }, [defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      setFieldError: (field: string, message: string) => {
        setError(field as keyof RoleFormData, { type: "server", message });
      },
      clearFieldErrors: () => clearErrors(),
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={control}
          label="Role Name"
          name="Name"
          error={errors.Name?.message}
          required
          placeholder="Enter role name"
          disabled={isLoading}
          onClear={() => clearErrors("Name")}
        />
      </form>
    );
  }
);

AddRoleForm.displayName = "AddRoleForm";

export default AddRoleForm;
